import React, {
  FocusEvent,
  InputHTMLAttributes,
  useEffect,
  useMemo,
  useState,
} from 'react';
import clsx from 'clsx';
import MaskedTextField from 'core/components/MaskedTextField';
import UnitedStateFlag from 'core/icons/unitedStateFlag';
import InputAdornment from '@mui/material/InputAdornment';
import {
  TextFieldProps,
} from '@mui/material/TextField';
import Tag from 'core/components/Tag';
import ToolTip from 'core/components/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import isNil from 'lodash/isNil';
import {
  PhoneNumberTagDTO,
} from 'dtos';
import type {
  MaskedInputValue,
} from 'core/components/formValue';

const mask = [{
  mask: '{+1} (000) 000-0000',
  lazy: false,
  overwrite: true,
}];

export type Props = TextFieldProps & {
  value?: MaskedInputValue;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  readonly?: boolean;
  error?: boolean;
  name: string;
  id: string;
  className?: string;
  inputClassName?: string;
  isLoadingPhoneTag?: boolean;
  phoneTag?: PhoneNumberTagDTO;
  onChange: (val: string) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  showErrorText?: boolean;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
};

export default function PhoneInput({
  value,
  placeholder,
  disabled,
  required,
  onChange,
  className,
  inputClassName,
  isLoadingPhoneTag,
  phoneTag,
  readonly: readOnly,
  error,
  showErrorText,
  inputProps,
  ...props
}: Props) {
  const classes = clsx(
    'populate-field-shell flex items-center w-full',
    { 'has-error': error },
  );

  const [isTagVisible, setIsTagVisible] = useState(false);

  useEffect(() => {
    if (isLoadingPhoneTag) {
      setIsTagVisible(false);
    } else if (!isNil(phoneTag)) {
      setIsTagVisible(false);
      requestAnimationFrame(() => setIsTagVisible(true));
    }
  }, [isLoadingPhoneTag, phoneTag]);

  const endAdornment = useMemo(() => {
    let adornmentChild;

    if (isLoadingPhoneTag) {
      adornmentChild = (
        <CircularProgress
          size={16}
        />
      );
    } else if (!isNil(phoneTag)) {
      adornmentChild = (
        <ToolTip title={phoneTag.tooltipMessage}>
          <div
            className={clsx(
              'transition-opacity duration-200 ease-in',
              isTagVisible ? 'opacity-100' : 'opacity-0',
            )}
          >
            <Tag
              tagName={phoneTag.type}
              className="!text-xs !font-medium rounded-full !whitespace-nowrap"
            />
          </div>
        </ToolTip>
      );
    }

    return isNil(adornmentChild)
      ? null
      : (
        <InputAdornment
          position="end"
          className="!h-full !max-h-none !m-0 !flex !items-center !py-1 !-mr-4 pr-2"
        >
          {adornmentChild}
        </InputAdornment>
      );
  }, [isLoadingPhoneTag, isTagVisible, phoneTag]);

  return (
    <div className={classes}>
      <MaskedTextField
        {...props}
        disabled={disabled}
        mask={mask}
        value={value ?? ''}
        onAccept={onChange}
        showErrorText={showErrorText}
        className={clsx('populate-phone-input', className)}
        maskedPosition={{ start: 3, end: 0 }}
        slotProps={{
          ...props.slotProps,
          htmlInput: {
            ...props.slotProps?.htmlInput,
            ...inputProps,
          },
          input: {
            ...props.slotProps?.input,
            startAdornment: (
              <InputAdornment position="start" className="-ml-1.5">
                <UnitedStateFlag className="!text-base" />
              </InputAdornment>
            ),
            endAdornment,
          },
        }}
      />
    </div>
  );
}
